import * as am4charts from "@amcharts/amcharts4/charts";
import * as am4core from "@amcharts/amcharts4/core";
import React, { forwardRef, useEffect, useRef } from "react";
import { applyAppFontFamily, configureNumberFormatter } from "../utils";
import {
  AxisConfig,
  ChartData,
  ChartDataValue,
  SimpleColumnChartProps,
} from "./types";
const DEFAULT_BAR_COLOR = "#4682B4";
const DIMMED_OPACITY = 0.3;

/**
 * Safely extracts numeric value from chart data
 */
const extractNumericValue = (
  item: ChartData,
  field: string
): ChartDataValue => {
  const rawValue = item[field];
  if (typeof rawValue === "number" && !isNaN(rawValue)) {
    return { value: rawValue, isValid: true };
  }
  if (typeof rawValue === "string") {
    const parsed = parseFloat(rawValue);
    if (!isNaN(parsed)) {
      return { value: parsed, isValid: true };
    }
  }
  return { value: 0, isValid: false };
};

const CHART_CONSTANTS = {
  LEGEND: { MARKER_WIDTH: 15, MARKER_HEIGHT: 3, MARGIN_BOTTOM: 20 },
  AXIS: { MIN_GRID_DISTANCE: 30 },
  DEFAULT_LABEL_MAX_WIDTH: 120,
  MIN_LABEL_WIDTH: 70,
  LABEL_PADDING: 2,
};

const handleAxisResize = (axis: am4charts.Axis) => {
  if (
    !axis.pixelWidth ||
    axis.endIndex === undefined ||
    axis.startIndex === undefined
  ) {
    return;
  }

  // Calculate available width per column
  const totalColumns = axis.endIndex - axis.startIndex + 1;
  const availableWidth = axis.pixelWidth;
  const widthPerColumn = availableWidth / totalColumns;

  const labelPadding = CHART_CONSTANTS.LABEL_PADDING;
  const maxLabelWidth = Math.max(
    widthPerColumn - labelPadding,
    CHART_CONSTANTS.MIN_LABEL_WIDTH
  );

  axis.renderer.labels.template.maxWidth = maxLabelWidth;
  axis.renderer.labels.template.truncate = true;
  axis.renderer.labels.template.wrap = false;

  axis.renderer.labels.template.paddingLeft = CHART_CONSTANTS.LABEL_PADDING;
  axis.renderer.labels.template.paddingRight = CHART_CONSTANTS.LABEL_PADDING;

  // Force labels to be redrawn
  axis.invalidateLabels();
};

const configureCategoryAxis = (
  chart: am4charts.XYChart,
  config: AxisConfig
) => {
  const categoryAxis = chart.xAxes.push(new am4charts.CategoryAxis());
  categoryAxis.dataFields.category = config.xAxisDataPoint;
  categoryAxis.title.text = config.xAxisLabel ?? "";
  categoryAxis.renderer.grid.template.location = 0;
  categoryAxis.renderer.minGridDistance =
    CHART_CONSTANTS.AXIS.MIN_GRID_DISTANCE;

  categoryAxis.renderer.grid.template.disabled = true;

  if (config.truncateXAxisLabels) {
    categoryAxis.renderer.labels.template.maxWidth =
      config.xAxisLabelMaxWidth || CHART_CONSTANTS.DEFAULT_LABEL_MAX_WIDTH;
    categoryAxis.renderer.labels.template.truncate = true;
    categoryAxis.renderer.labels.template.wrap = false;
    categoryAxis.renderer.labels.template.textAlign = "middle";

    categoryAxis.events.on("sizechanged", function (ev) {
      const axis = ev.target;
      handleAxisResize(axis);
    });

    chart.events.on("datavalidated", function () {
      handleAxisResize(categoryAxis);
    });

    categoryAxis.renderer.labels.template.tooltipText = config.hideXAxisTooltip
      ? ""
      : (config.xAxisTooltipFormat ?? "{category}");

    categoryAxis.renderer.labels.template.paddingLeft = 5;
    categoryAxis.renderer.labels.template.paddingRight = 5;
  }

  return categoryAxis;
};

const configureValueAxis = (
  chart: am4charts.XYChart,
  config: AxisConfig & {
    maxCap?: number;
    yGridDistance?: number;
    extraSpacingForLabels?: boolean;
  },
  yAxisDataPoint?: string
) => {
  const valueAxis = chart.yAxes.push(new am4charts.ValueAxis());
  valueAxis.title.text = config.yAxisLabel || "";

  if (config.yAxisNumberFormat) {
    valueAxis.numberFormatter = new am4core.NumberFormatter();
    valueAxis.numberFormatter.numberFormat = config.yAxisNumberFormat;
  }

  if (config.yGridDistance) {
    valueAxis.renderer.minGridDistance = config.yGridDistance;
  }

  // Calculate dynamic maximum based on actual data
  if (config.maxCap && yAxisDataPoint) {
    // Safely extract numeric values from chart data
    const dataValues = (chart.data as ChartData[])
      .map((item) => extractNumericValue(item, yAxisDataPoint))
      .filter((result) => result.isValid)
      .map((result) => result.value);

    // Handle empty dataset case
    if (dataValues.length === 0) {
      console.warn("No valid numeric data found for Y-axis calculation");
      valueAxis.max = config.maxCap;
      valueAxis.min = 0;
      valueAxis.strictMinMax = true;
      return valueAxis;
    }

    const dataMax = Math.max(...dataValues, 0);

    // Add buffer (15% for extra spacing, 10% otherwise)
    const buffer = config.extraSpacingForLabels ? 0.15 : 0.1;
    const dynamicMax = dataMax * (1 + buffer);

    // Only cap at maxCap if dynamic maximum would exceed it
    if (dynamicMax > config.maxCap) {
      if (config.extraSpacingForLabels) {
        valueAxis.max = config.maxCap + 15;
        valueAxis.strictMinMax = true;
      } else {
        valueAxis.max = config.maxCap;
        valueAxis.strictMinMax = true;
      }
    } else {
      valueAxis.max = dynamicMax;
      valueAxis.strictMinMax = true;
    }

    valueAxis.min = 0;
  } else {
    // For non-capped charts, use extraMax for spacing
    if (config.extraSpacingForLabels) {
      valueAxis.extraMax = 0.15;
    } else {
      valueAxis.extraMax = 0.1;
    }
  }

  return valueAxis;
};

const configureLegend = (chart: am4charts.XYChart, xAxisDataPoint: string) => {
  chart.legend = new am4charts.Legend();
  chart.legend.itemContainers.template.togglable = true;
  chart.legend.itemContainers.template.cursorOverStyle =
    am4core.MouseCursorStyle.default;
  chart.legend.position = "bottom";
  chart.legend.contentAlign = "right";
  chart.legend.marginTop = CHART_CONSTANTS.LEGEND.MARGIN_BOTTOM;
  chart.legend.markers.template.width = CHART_CONSTANTS.LEGEND.MARKER_WIDTH;
  chart.legend.markers.template.height = CHART_CONSTANTS.LEGEND.MARKER_HEIGHT;
  const originalData = [...chart.data];

  chart.legend.itemContainers.template.events.on("over", (ev) => {
    const legendDataItem = ev.target.dataItem?.dataContext as {
      name: string;
      column?: am4charts.Column;
    };

    if (legendDataItem && typeof legendDataItem.name === "string") {
      const categoryValueHovered = legendDataItem.name;
      // Dim all columns
      chart.series.each((series) => {
        if (series instanceof am4charts.ColumnSeries) {
          series.columns.each((column) => {
            column.adapter.add("fillOpacity", () => DIMMED_OPACITY);
          });
        }
      });

      // Highlight the hovered column
      chart.series.each((series) => {
        if (series instanceof am4charts.ColumnSeries) {
          series.columns.each((column) => {
            const columnCategory = (
              column.dataItem as am4charts.ColumnSeriesDataItem
            )?.categoryX;
            if (columnCategory === categoryValueHovered) {
              column.adapter.remove("fillOpacity");
              column.animate(
                { property: "scale", from: 1, to: 1.05 },
                200,
                am4core.ease.quadOut
              );
              if (column.children.length > 0) {
                column.children.each((child) => {
                  if (child instanceof am4core.Label) {
                    child.fontSize = 14; // Slightly increase label size
                    child.fontWeight = "bold";
                  }
                });
              }
            }
          });
        }
      });
    }
  });

  chart.legend.itemContainers.template.events.on("out", () => {
    // Restore all columns to normal
    chart.series.each((series) => {
      if (series instanceof am4charts.ColumnSeries) {
        series.columns.each((column) => {
          column.adapter.remove("fillOpacity");
          column.animate(
            { property: "scale", to: 1 },
            200,
            am4core.ease.quadOut
          );
          if (column.children.length > 0) {
            column.children.each((child) => {
              if (child instanceof am4core.Label) {
                child.fontSize = 12;
                child.fontWeight = "normal";
              }
            });
          }
        });
      }
    });
  });

  chart.legend.itemContainers.template.events.on("hit", (ev) => {
    const legendDataItem = ev.target.dataItem?.dataContext as { name: string };

    if (legendDataItem && typeof legendDataItem.name === "string") {
      const categoryValueClicked = legendDataItem.name;
      const { isActive } = ev.target;

      if (isActive) {
        const currentlyVisibleCategories = new Set(
          chart.data.map((item) => item[xAxisDataPoint])
        );
        currentlyVisibleCategories.add(categoryValueClicked);
        chart.data = originalData.filter((item) =>
          currentlyVisibleCategories.has(item[xAxisDataPoint])
        );
      } else {
        chart.data = chart.data.filter(
          (item) => item[xAxisDataPoint] !== categoryValueClicked
        );
      }

      // Trigger resize after data update
      chart.events.once("datavalidated", function () {
        const categoryAxis = chart.xAxes.getIndex(0);
        if (categoryAxis) {
          handleAxisResize(categoryAxis);
        }
      });
    }
  });

  return chart.legend;
};

const getColumnFill = (colorMap: Record<string, string>, category: string) => {
  return am4core.color(colorMap[category] || DEFAULT_BAR_COLOR);
};

const addLabelBullet = (
  series: am4charts.ColumnSeries,
  labelFormat?: string
) => {
  const valueLabel = series.columns.template.createChild(am4core.Label);
  valueLabel.text = labelFormat ?? "{valueY}";
  valueLabel.valign = "top";
  valueLabel.align = "center";
  valueLabel.dy = -16;
  valueLabel.fontSize = 12;
  valueLabel.fill = am4core.color("#000");
  valueLabel.truncate = false;
};

const configureSeries = (
  chart: am4charts.XYChart,
  config: Pick<
    SimpleColumnChartProps,
    | "yAxisDataPoint"
    | "xAxisDataPoint"
    | "xAxisLabel"
    | "barColor"
    | "columnLabelFormat"
    | "columnTooltipFormat"
    | "lightenFactor"
    | "hideColumnLabel"
    | "addStripePattern"
  >,
  colorMap: Record<string, string>
) => {
  const series = chart.series.push(new am4charts.ColumnSeries());
  series.dataFields.valueY = config.yAxisDataPoint;
  series.dataFields.categoryX = config.xAxisDataPoint;
  series.name = config.xAxisLabel ?? "";
  series.columns.template.minHeight = 10;
  series.columns.template.strokeWidth = 0;
  series.columns.template.tooltipText =
    config.columnTooltipFormat ?? "{categoryX}: [bold]{valueY}[/]";
  series.columns.template.tooltipY = 0;

  series.columns.template.states.create("hover");
  series.columns.template.states.create("active");

  series.columns.template.defaultState.transitionDuration = 150;

  if (series.tooltip) {
    series.tooltip.pointerOrientation = "vertical";
    series.tooltip.dy = -5;
  }
  if (!config.hideColumnLabel) {
    addLabelBullet(series, config.columnLabelFormat);
  }
  series.columns.template.adapter.add("fill", (_, target) => {
    const isEven = ((target.dataItem?.index ?? 0) + 1) % 2 === 0;
    const isOdd = !isEven;
    const isAll = config.addStripePattern === "all";
    const shouldAddPattern =
      (isEven && config.addStripePattern === "even") ||
      (isOdd && config.addStripePattern === "odd") ||
      isAll;
    const mainColor = getColumnFill(
      colorMap,
      target.dataItem?.categories.categoryX ?? ""
    );

    if (shouldAddPattern) {
      const pattern = new am4core.LinePattern();
      pattern.strokeWidth = 6;
      pattern.gap = 10;
      const size = 25;
      pattern.width = size + pattern.gap;
      pattern.height = size + pattern.gap;
      pattern.rotation = -25;
      pattern.stroke = mainColor.brighten(1);
      pattern.strokeOpacity = 0.5;
      pattern.backgroundFill = mainColor;
      pattern.backgroundOpacity = 1;
      return pattern;
    }

    return mainColor;
  });

  return series;
};

/**
 * A simple column chart component.
 * @param {SimpleColumnChartProps} props - The props for the component.
 * @returns {React.JSX.Element} The simple column chart component.
 */
const SimpleColumnChart = forwardRef<am4charts.XYChart, SimpleColumnChartProps>(
  (props, ref) => {
    const {
      id,
      data,
      yAxisDataPoint,
      xAxisDataPoint,
      xAxisLabel,
      yAxisLabel,
      yAxisNumberFormat,
      barColor = DEFAULT_BAR_COLOR,
      columnLabelFormat,
      columnTooltipFormat,
      lightenFactor,
      truncateXAxisLabels,
      xAxisLabelMaxWidth,
      xAxisTooltipFormat,
      hideXAxisTooltip,
      hideColumnLabel,
      addStripePattern,
      hideLegend,
      fixedMaximum,
      yGridDistance,
      extraSpacingForLabels,
    } = props;

    const chartRef = useRef<am4charts.XYChart | null>(null);

    useEffect(() => {
      const chart: am4charts.XYChart = am4core.create(id, am4charts.XYChart);
      if (ref) {
        (ref as React.MutableRefObject<am4charts.XYChart>).current = chart;
      }
      chart.data = data;
      configureNumberFormatter(chart);

      const colorMap: Record<string, string> = {};
      let index = 0;
      for (const item of data) {
        const category = item[xAxisDataPoint];
        colorMap[category] = Array.isArray(barColor)
          ? barColor[index % barColor.length]
          : barColor;
        index++;
      }

      configureCategoryAxis(chart, {
        xAxisDataPoint,
        xAxisLabel,
        truncateXAxisLabels,
        xAxisLabelMaxWidth,
        xAxisTooltipFormat,
        hideXAxisTooltip,
      });
      configureValueAxis(
        chart,
        {
          yAxisLabel,
          yAxisNumberFormat,
          maxCap: fixedMaximum,
          yGridDistance,
          extraSpacingForLabels,
        },
        yAxisDataPoint
      );
      if (hideLegend !== true) {
        configureLegend(chart, xAxisDataPoint);
      }
      const series = configureSeries(
        chart,
        {
          yAxisDataPoint,
          xAxisDataPoint,
          xAxisLabel,
          barColor,
          columnLabelFormat,
          columnTooltipFormat,
          lightenFactor,
          hideColumnLabel,
          addStripePattern,
        },
        colorMap
      );

      applyAppFontFamily(chart);

      series.events.on("ready", () => {
        /* eslint-disable @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        const legendData = [];
        series.columns.each(function (column) {
          legendData.push({
            name: column.dataItem?.categories.categoryX ?? "",
            fill: column.fill,
            series,
            column,
          });
        });

        if (chart.legend) {
          // @ts-ignore
          chart.legend.data = legendData;
        }
        /* eslint-enable @typescript-eslint/ban-ts-comment */
      });

      chartRef.current = chart;

      return () => {
        chart.dispose();
      };
    }, [
      barColor,
      columnLabelFormat,
      columnTooltipFormat,
      data,
      hideColumnLabel,
      hideXAxisTooltip,
      id,
      lightenFactor,
      truncateXAxisLabels,
      xAxisDataPoint,
      xAxisLabel,
      xAxisLabelMaxWidth,
      xAxisTooltipFormat,
      yAxisDataPoint,
      yAxisLabel,
      yAxisNumberFormat,
      addStripePattern,
      ref,
      fixedMaximum,
      yGridDistance,
      hideLegend,
      extraSpacingForLabels,
    ]);

    return <div id={id} style={{ width: "100%", height: "100%" }} />;
  }
);

export default SimpleColumnChart;
